/**
 * useExchangeRates Hook
 *
 * React hook for managing exchange rates in components
 * Automatically fetches rates on mount and provides utilities for currency conversion
 */

import { useState, useEffect, useCallback } from 'react';
import { getExchangeRates, areRatesStale, refreshExchangeRates, SUPPORTED_CURRENCIES } from '../services/exchangeRateService';
import { useAuth } from '../AuthContext';

/**
 * Hook for managing exchange rates
 *
 * @returns {Object} {
 *   rates: Exchange rates object,
 *   loading: Boolean indicating if rates are being fetched,
 *   error: Error object if fetch failed,
 *   lastUpdated: Timestamp of last update,
 *   isStale: Boolean indicating if rates need refresh,
 *   refresh: Function to manually refresh rates,
 *   supportedCurrencies: Object with supported currency info
 * }
 */
export const useExchangeRates = () => {
  const { currentUser } = useAuth();

  // ⭐ FIX: Start with fallback rates immediately (not null)
  // This prevents race condition where portfolio calculates before rates load
  const [rates, setRates] = useState({
    base: 'EUR',
    date: new Date().toISOString().split('T')[0],
    rates: {
      USD: 1.09,
      GBP: 0.86,
      EUR: 1.0
    },
    timestamp: Date.now(),
    isFallback: true,
    initialFallback: true
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch exchange rates
  const fetchRates = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      let fetchedRates;

      if (forceRefresh) {
        // Force refresh from API
        fetchedRates = await refreshExchangeRates(currentUser?.uid);
      } else {
        // Use cached if available
        fetchedRates = await getExchangeRates(currentUser?.uid);
      }

      console.log('🔄 [useExchangeRates] Fetched rates:', fetchedRates);

      // Always set rates even if they're fallback
      if (fetchedRates && fetchedRates.rates) {
        setRates(fetchedRates);
        setLastUpdated(fetchedRates.timestamp || Date.now());
        if (fetchedRates.isFallback) {
          console.warn('⚠️ [useExchangeRates] Using fallback rates in hook');
        }
      } else {
        console.error('❌ [useExchangeRates] Invalid rates object received:', fetchedRates);
      }
    } catch (err) {
      console.error('❌ [useExchangeRates] Error fetching exchange rates:', err);
      setError(err);

      // Even on error, try to use hardcoded fallback
      console.warn('⚠️ [useExchangeRates] Setting hardcoded fallback in hook');
      setRates({
        base: 'EUR',
        date: new Date().toISOString().split('T')[0],
        rates: {
          USD: 1.09,
          GBP: 0.86,
          EUR: 1.0
        },
        timestamp: Date.now(),
        isFallback: true,
        hookFallback: true
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load rates on mount
  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Check if rates are stale every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (areRatesStale() && !loading) {
        console.log('📊 Exchange rates are stale, refreshing...');
        fetchRates();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [loading, fetchRates]);

  // Manual refresh function
  const refresh = useCallback(async () => {
    await fetchRates(true);
  }, [fetchRates]);

  return {
    rates,
    loading,
    error,
    lastUpdated,
    isStale: areRatesStale(),
    refresh,
    supportedCurrencies: SUPPORTED_CURRENCIES
  };
};

export default useExchangeRates;
